import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { Adjustment } from '../models/adjustment';
import { AdjustmentHttpService } from './adjustment.http.service';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { ReservationStore } from './reservation-store';
import { ReservationStatusCode } from '../models/reservation-status-code.enum';
import { tap } from 'rxjs/operators';
import { AdjustmentTypeStore } from './adjustment-type/adjustment-type.store';
import { AdjustmentSign } from '../models/adjustment-sign.enum';
import { RsvpStore } from './rsvp.store';

@Injectable()
export class AdjustmentStore {

  private adjustmentList: ReplaySubject<List<Adjustment>> = new ReplaySubject<List<Adjustment>>(1);
  private adjustmentArray: ReplaySubject<Adjustment[]> = new ReplaySubject<Adjustment[]>(1);

  constructor(
    private adjustmentHttpService: AdjustmentHttpService,
    private flashMessagesService: FlashMessagesService,
    private reservationStore: ReservationStore,
    private rsvpStore: RsvpStore,
    private adjustmentTypeStore: AdjustmentTypeStore
  ) { }

  addAdjustment(adjustment: Adjustment): Observable<Adjustment> {
    return this.adjustmentHttpService.addAdjustment(adjustment).pipe(
      tap((addedAdjustment: Adjustment) => {
        const adjustmentSign = adjustment.adjustmentType.credit ? AdjustmentSign.CREDIT : AdjustmentSign.DEBIT;
        this.adjustmentTypeStore.getAdjustmentType(addedAdjustment.adjustmentType.id, adjustmentSign)
          .take(1)
          .subscribe((adjustmentType) => {
            this.flashMessagesService.success(
              'EVENT.ADJUSTMENT.ADJUSTMENT_SAVED', null, {
                'type': adjustmentType.name,
                'description': addedAdjustment.description ? addedAdjustment.description : ''
              });
          });
      })
    )
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_ADJUSTMENT', `Error adding adjustment: ${error}`);
        return Observable.throw(error);
      });
  }

  get adjustments(): Observable<List<Adjustment>> {
    return this.adjustmentList.asObservable();
  }

  get adjustmentsAsArray(): Observable<Adjustment[]> {
    return this.adjustmentArray.asObservable();
  }

  loadAdjustments(eventId: number): Observable<List<Adjustment>> {
    return this.adjustmentHttpService.getAdjustments(eventId)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.COULD_NOT_GET_PAYMENTS', `Error getting adjustments: ${error}`);
        return Observable.throw(error);
      })
      .map((result: List<Adjustment>) => {
        const sortedIterable = result.sort(
          (adjustment1, adjustment2) => {
            if (adjustment1.createdDate < adjustment2.createdDate) {
              return -1;
            } else if (adjustment1.createdDate > adjustment2.createdDate) {
              return 1;
            } else {
              return 0;
            }
          });
        this.adjustmentList.next(sortedIterable.toList());
        this.adjustmentArray.next(sortedIterable.toArray());
        return result;
      });
  }

  deleteAdjustment(adjustmentId: number, eventId: number): Observable<any> {
    return this.adjustmentHttpService.deleteAdjustment(adjustmentId)
      .catch((error: any) => {
        this.flashMessagesService.error('MESSAGES.ERROR.DELETE_ADJUSTMENT', `Error deleting adjustment: ${error}`);
        return Observable.of(error);
      })
      .map(res => {
        this.loadAdjustments(eventId).subscribe();
        return res;
      });
  }

  getCalculatedPayment(reservationStatusCode: ReservationStatusCode, subscriber: boolean): Observable<number> {
    let totalCommissions = 0;
    const obs$ = subscriber ?
      this.reservationStore.getTotalCommissions(reservationStatusCode) : this.rsvpStore.totalCommission;

    return obs$.flatMap(total => {
      totalCommissions = total;
      return this.adjustments;
    }).map((list: List<Adjustment>) => {
      const adjustmentTotal = list.reduce((sum: number, adjustment: Adjustment) => {
        return sum + adjustment.adjustmentAmount;
      }, 0);
      return adjustmentTotal + totalCommissions;
    });
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/GroupPay/jobs/QA2/jobs/QA-Pioneer-ALL-2-POC/workspace/pioneer-web/src/app/event/service/adjustment.store.ts